import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { Assignment } from '@deltek/specification-client/core/Models/assignment'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import {
    ProjectElementMetadataInput,
    UpdateElementInput,
} from '@deltek/specification-client/core/Models/projectElementInput'
import { ProjectElementMetadata } from '@deltek/specification-client/core/Models/projectElementMetadata'
import { User } from '@deltek/specification-client/core/Models/user'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { AllProjectResponse } from 'Models/project'
import { AllUsersResponse } from 'Models/user'
import {
    UpdateProjectNuggetElementMutation,
    UpdateProjectProductFamilyElementMutation,
    UpdateProjectUniformatClassificationElementMutation,
} from 'api/graphQL/mutations/projectElement.mutations'
import { GetProjectTeamQuery } from 'api/graphQL/queries/project.queries'
import { GetAllUsersQuery } from 'api/graphQL/queries/user.queries'
import {
    setFamilyProjectElementMetadata,
    setProjectElementMetadata,
    workspaceSelectedElement,
} from 'api/graphQL/reactiveVars/element'
import {
    UpdateProjectElementPropertiesResponse,
    UpdateProjectNuggetElementResponse,
    UpdateProjectProductFamilyElementResponse,
} from 'api/graphQL/types/projectElement.types'
import clsx from 'clsx'
import React from 'react'
import { useParams } from 'react-router-dom'
import { LODS, STATUS } from 'shared/utils/dropdownUtils'
import { getStatePUCPropertyValue } from 'shared/utils/workspaceUtils'
import AutoCompleteElementProperties from '../AutoCompleteElementProperties'
import { WorkspaceContentContext } from '../WorkspaceContentContext'

const elementPropertiesStyles = makeStyles((theme: Theme) => ({
    elementPropertiesCont: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    propertiesLabel: {
        color: '#304959',
        textTransform: 'uppercase',
        lineHeight: pxToRem(16),
        fontSize: pxToRem(12),
    },
}))

interface AssignedUserList {
    userName: string
    userId?: string | null
}

const defaultSelectedValues: ProjectElementMetadataInput = {
    assignedUserId: null,
    state: 'NEW',
    currentLOD: 100,
    targetLOD: 100,
}

// Hide LOS fields for editing properties to address existing projects or
// Level of Specification automation does not work.
const isLOSVisible = false

interface CommonElementPropertiesInterface {
    projectElementMetadata?: Partial<ProjectElementMetadata>
    familyProjectMetadata?: Partial<ProjectElementMetadata>
    projectElementNode?: ProjectElementNode
    contentTeamFromStudioPage?: boolean
    isOMWorkspace?: boolean
    setLoadingBackdrop?: (value: boolean) => void
}

const CommonElementProperties: React.FC<CommonElementPropertiesInterface> = ({
    projectElementMetadata,
    familyProjectMetadata,
    projectElementNode,
    contentTeamFromStudioPage,
    isOMWorkspace,
    setLoadingBackdrop,
}) => {
    const { handleRetrieveUpdatedDtvPerPartType } = React.useContext(
        WorkspaceContentContext
    )
    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )

    const { projectId } = useParams<{ projectId?: string }>()
    const classes = elementPropertiesStyles()
    const [assignedUsersList, setAssignedUsersList] = React.useState<
        AssignedUserList[]
    >([{ userName: 'Unassigned', userId: null }])

    const [currentSelectedValues, setCurrentSelectedValues] = React.useState<
        ProjectElementMetadataInput
    >(defaultSelectedValues)

    const isFamilyElement = React.useMemo(
        () => selectedProjectElementNode?.elementType === 'PRODUCTFAMILY',
        [selectedProjectElementNode]
    )

    const [getProjectData] = useLazyQuery<AllProjectResponse>(
        GetProjectTeamQuery,
        {
            onCompleted: (projectData) => {
                if (projectData) {
                    let usersList: AssignedUserList[] = []
                    projectData.getProject.projectTeam?.assignments?.forEach(
                        (assignedUser: Partial<Assignment>) => {
                            const obj = {
                                userName:
                                    assignedUser.user?.profileInfo?.firstName +
                                    ' ' +
                                    assignedUser.user?.profileInfo?.lastName,
                                userId: assignedUser.user?.id,
                            }
                            usersList.push(obj)
                        }
                    )
                    usersList.push({
                        userName: 'Unassigned',
                        userId: null,
                    })
                    usersList = usersList.sort(
                        (a, b) => -b.userName.localeCompare(a.userName)
                    )
                    setAssignedUsersList(usersList)
                }
            },
        }
    )

    const [getAllUsers] = useLazyQuery<AllUsersResponse>(GetAllUsersQuery, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            if (data) {
                let usersList: AssignedUserList[] = []
                data.getAllUsers.users.forEach(
                    (assignedUser: Partial<User>) => {
                        const obj = {
                            userName:
                                assignedUser?.profileInfo?.firstName +
                                ' ' +
                                assignedUser?.profileInfo?.lastName,
                            userId: assignedUser?.id,
                        }
                        usersList.push(obj)
                    }
                )
                usersList.push({
                    userName: 'Unassigned',
                    userId: null,
                })
                usersList = usersList.sort(
                    (a, b) => -b.userName.localeCompare(a.userName)
                )
                setAssignedUsersList(usersList)
            }
        },
    })

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [updatePUCElementProperties] = useMutation<
        UpdateProjectElementPropertiesResponse,
        { input: UpdateElementInput }
    >(UpdateProjectUniformatClassificationElementMutation, {
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message ?? '',
                    variant: 'error',
                    id: 'ProjectWorkspaceContentArea_PUCElement',
                },
            })
        },
    })

    const [updateProjectProductFamilyElement] = useMutation<
        UpdateProjectProductFamilyElementResponse,
        { input: UpdateElementInput }
    >(UpdateProjectProductFamilyElementMutation, {
        onCompleted: () => {
            // When the target LOS of family element is updated, the DTVs in all the tabs/part types should be updated
            // to reflect the new contents visibility based on the updated target LOS.
            // Note: Had to call repeated queries as a workaround to update the DTVs in all the tabs/part types.
            // This is due to Apollo's behavior issue where multiple queries use same underlying data.
            // This will be fixed if we upgrade in the new version of Apollo.
            if (contentTeamFromStudioPage && !isOMWorkspace) {
                handleRetrieveUpdatedDtvPerPartType?.('ALL')
            } else {
                handleRetrieveUpdatedDtvPerPartType?.('GENERAL')
                handleRetrieveUpdatedDtvPerPartType?.('PRODUCTS')
                handleRetrieveUpdatedDtvPerPartType?.('EXECUTION')
            }
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message ?? '',
                    variant: 'error',
                    id:
                        'ProjectWorkspaceContentArea_ProjectProductFamilyElement',
                },
            })
        },
    })

    const [updateProjectNuggetElement] = useMutation<
        UpdateProjectNuggetElementResponse,
        { input: UpdateElementInput }
    >(UpdateProjectNuggetElementMutation, {
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message ?? '',
                    variant: 'error',
                    id: 'ProjectWorkspaceContentArea_ProjectNuggetElement',
                },
            })
        },
    })

    const handleUpdatePUCElementProperties = React.useCallback(
        (finalSelectedValues: ProjectElementMetadataInput) => {
            const inputValues = {
                variables: {
                    input: {
                        projectElementMetadataInput: finalSelectedValues,
                        elementId: projectElementNode?.elementId ?? '',
                        projectId: projectId ?? '',
                    },
                },
            }
            if (projectElementNode?.elementType === 'PRODUCTFAMILY') {
                if (setLoadingBackdrop) setLoadingBackdrop(true)
            }

            projectElementNode?.elementType === 'ASSEMBLY'
                ? updatePUCElementProperties(inputValues)
                : projectElementNode?.elementType === 'PRODUCTFAMILY'
                ? updateProjectProductFamilyElement(inputValues)
                : updateProjectNuggetElement(inputValues)
        },
        [
            projectElementNode,
            projectId,
            updateProjectNuggetElement,
            updateProjectProductFamilyElement,
            updatePUCElementProperties,
        ]
    )

    const handleChange = (paramName: string, paramValue: string | null) => {
        let finalValue: string | number | null = paramValue
        if (paramName === 'assignedUserId') {
            finalValue =
                assignedUsersList.find((user) => user.userName === paramValue)
                    ?.userId ?? defaultSelectedValues.assignedUserId
        } else if (paramName === 'currentLOD' || paramName === 'targetLOD') {
            if (paramValue) {
                finalValue = parseInt(paramValue)
            } else {
                finalValue =
                    paramName === 'currentLOD'
                        ? defaultSelectedValues.currentLOD
                        : defaultSelectedValues.targetLOD
            }
        } else if (paramName === 'state') {
            if (!paramValue) {
                finalValue = defaultSelectedValues.state
            } else {
                finalValue = STATUS.filter(
                    (status) =>
                        status.value === getStatePUCPropertyValue(paramValue)
                )[0].value
            }
        }
        const finalSelectedValues: ProjectElementMetadataInput = {
            ...currentSelectedValues,
            [paramName]: finalValue,
        }

        handleUpdatePUCElementProperties(finalSelectedValues)
        setCurrentSelectedValues(finalSelectedValues)
        updateProjectElementMetaDataState(finalSelectedValues)
    }

    const updateProjectElementMetaDataState = (
        selectedValues: ProjectElementMetadataInput
    ) => {
        const assignedUserName = assignedUsersList.find(
            (user) => user.userId === selectedValues.assignedUserId
        )?.userName

        const stateName = STATUS.filter(
            (status) => status.value === selectedValues.state
        )[0].name

        setProjectElementMetadata({
            ...projectElementMetadata,
            assignedUserId:
                selectedValues.assignedUserId ??
                defaultSelectedValues.assignedUserId,
            assignedUser: assignedUserName,
            currentLOD:
                selectedValues.currentLOD ?? defaultSelectedValues.currentLOD,
            targetLOD:
                selectedValues.targetLOD ?? defaultSelectedValues.targetLOD,
            state: stateName ?? defaultSelectedValues.state,
        })

        if (isFamilyElement) {
            setFamilyProjectElementMetadata({
                ...familyProjectMetadata,
                assignedUserId:
                    selectedValues.assignedUserId ??
                    defaultSelectedValues.assignedUserId,
                assignedUser: assignedUserName,
                currentLOD:
                    selectedValues.currentLOD ??
                    defaultSelectedValues.currentLOD,
                targetLOD:
                    selectedValues.targetLOD ?? defaultSelectedValues.targetLOD,
                state: stateName ?? defaultSelectedValues.state,
                isTargetLevelOfSpecificationManuallyUpdated: true,
            })
        }
    }

    React.useEffect(() => {
        if (projectElementNode) {
            if (projectElementMetadata && projectElementNode.elementId) {
                const selectedValues: ProjectElementMetadataInput = {
                    assignedUserId:
                        projectElementMetadata?.assignedUserId ??
                        defaultSelectedValues.assignedUserId,
                    state:
                        getStatePUCPropertyValue(
                            projectElementMetadata?.state
                        ) ?? defaultSelectedValues.state,
                    currentLOD:
                        projectElementMetadata?.currentLOD ??
                        defaultSelectedValues.currentLOD,
                    targetLOD:
                        projectElementMetadata?.targetLOD ??
                        defaultSelectedValues.targetLOD,
                }
                setCurrentSelectedValues(selectedValues)
            } else {
                setCurrentSelectedValues(defaultSelectedValues)
            }
        }
    }, [projectElementNode, projectElementMetadata, setCurrentSelectedValues])

    React.useEffect(() => {
        if (contentTeamFromStudioPage) {
            getAllUsers({})
        } else {
            getProjectData({
                variables: {
                    projectId: projectId,
                },
            })
        }
    }, [getAllUsers, getProjectData, contentTeamFromStudioPage])

    return (
        <div
            className={clsx(
                classes.elementPropertiesCont,
                'commonElementPropertiesClass'
            )}
        >
            {(!contentTeamFromStudioPage || isOMWorkspace) && (
                <>
                    <Typography
                        className={classes.propertiesLabel}
                        variant="body2"
                    >
                        ASSIGNED TO
                    </Typography>
                    <AutoCompleteElementProperties
                        isDisabled={!Boolean(projectElementNode?.elementId)}
                        placeholderText={'Select Team Member'}
                        dropdownList={assignedUsersList.map(
                            (users) => users.userName
                        )}
                        parameterName={'assignedUserId'}
                        data-testid="assignedToTestId"
                        selectedValue={
                            assignedUsersList.find((item) =>
                                currentSelectedValues.assignedUserId
                                    ? item.userId ===
                                      currentSelectedValues.assignedUserId
                                    : !item.userId
                            )?.userName ?? null
                        }
                        handleChange={handleChange}
                    />
                    <Typography
                        className={classes.propertiesLabel}
                        variant="body2"
                    >
                        STATE
                    </Typography>
                    <AutoCompleteElementProperties
                        isDisabled={!Boolean(projectElementNode?.elementId)}
                        placeholderText={'Select State'}
                        dropdownList={STATUS.map((item) => item.name)}
                        parameterName={'state'}
                        data-testid="stateTestId"
                        selectedValue={
                            STATUS.filter(
                                (state) =>
                                    state.value === currentSelectedValues.state
                            )[0].name
                        }
                        handleChange={handleChange}
                    />
                </>
            )}

            <Typography className={classes.propertiesLabel} variant="body2">
                CURRENT Level of Specification (LOS)
            </Typography>
            <AutoCompleteElementProperties
                isDisabled={!Boolean(projectElementNode?.elementId)}
                placeholderText={'Assign Current LOS'}
                dropdownList={LODS}
                parameterName={'currentLOD'}
                data-testid="currentLODTestId"
                selectedValue={currentSelectedValues.currentLOD.toString()}
                handleChange={handleChange}
            />
            {!isOMWorkspace ? (
                <>
                    <Typography
                        className={classes.propertiesLabel}
                        variant="body2"
                    >
                        TARGET Level of Specification (LOS)
                    </Typography>
                    <AutoCompleteElementProperties
                        isDisabled={!Boolean(projectElementNode?.elementId)}
                        placeholderText="Assign Target LOS"
                        dropdownList={LODS}
                        parameterName={'targetLOD'}
                        data-testid="targetLODTestId"
                        selectedValue={currentSelectedValues.targetLOD.toString()}
                        handleChange={handleChange}
                    />
                </>
            ) : null}
        </div>
    )
}

export default CommonElementProperties
